import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Skeleton,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import axiosClient from "../../api/AxiosClient";
import styles from "./styles.module.css";

interface MappingRecord {
  id: number;
  job_title: string;
  level_title: string;
  job_family: string;
  sub_family: string;
  grade_level: number;
  ctc_midpoint_aed: number;
}

interface PaginationInfo {
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

interface ApiResponse {
  data: MappingRecord[];
  pagination: PaginationInfo;
}

interface RateCardAdministrationProps {
  onBack?: () => void;
}

type DialogMode = "add" | "edit" | "delete" | null;

export default function RateCardAdministration({
  onBack,
}: RateCardAdministrationProps): JSX.Element {
  // State management
  const [records, setRecords] = useState<MappingRecord[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo>({
    total: 0,
    limit: 15,
    offset: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Filter states
  const [jobFamilyFilter, setJobFamilyFilter] = useState("");
  const [subFamilyFilter, setSubFamilyFilter] = useState("");

  // Sorting states
  const [sortBy, setSortBy] = useState<string>("job_family");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Debounced filter values
  const [debouncedJobFamily, setDebouncedJobFamily] = useState("");
  const [debouncedSubFamily, setDebouncedSubFamily] = useState("");

  // Dialog state (for Add and Delete only)
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedRecord, setSelectedRecord] = useState<MappingRecord | null>(
    null,
  );
  const [formData, setFormData] = useState<Partial<MappingRecord>>({});
  const [submitting, setSubmitting] = useState(false);

  // Inline editing state
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editedCtc, setEditedCtc] = useState<number>(0);
  const [savingCtc, setSavingCtc] = useState(false);

  // Debounce filter inputs (500ms)
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedJobFamily(jobFamilyFilter), 500);
    return () => clearTimeout(timer);
  }, [jobFamilyFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSubFamily(subFamilyFilter), 500);
    return () => clearTimeout(timer);
  }, [subFamilyFilter]);

  // Fetch records from API
  const fetchRecords = useCallback(
    async (offset: number = 0, limit?: number) => {
      try {
        setLoading(true);
        setError(null);

        // Use provided limit or fall back to current pagination limit
        const currentLimit = limit ?? pagination.limit;

        const params = new URLSearchParams({
          limit: currentLimit.toString(),
          offset: offset.toString(),
        });

        // Add sorting parameters
        if (sortBy) {
          params.append("sort_by", sortBy);
          params.append("sort_order", sortOrder);
        }

        // Add filters if present
        if (debouncedJobFamily.trim()) {
          params.append("job_family", debouncedJobFamily.trim());
        }
        if (debouncedSubFamily.trim()) {
          params.append("sub_family", debouncedSubFamily.trim());
        }

        const response = await axiosClient.get<ApiResponse>(
          `/commercial-calculator-rate-card?${params.toString()}`,
        );

        const data = response.data.data || [];
        const paginationData = response.data.pagination;

        setRecords(data);

        // Use pagination data from API response
        setPagination({
          total: paginationData.total,
          limit: paginationData.limit,
          offset: paginationData.offset,
          hasMore: paginationData.hasMore,
        });
      } catch (err) {
        console.error("Error fetching records:", err);
        setError("Failed to load rate card mappings. Please try again.");
        setRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [
      pagination.limit,
      sortBy,
      sortOrder,
      debouncedJobFamily,
      debouncedSubFamily,
    ],
  );

  // Initial fetch and refetch on filter/sort/limit change
  useEffect(() => {
    fetchRecords(0);
  }, [
    debouncedJobFamily,
    debouncedSubFamily,
    sortBy,
    sortOrder,
    pagination.limit,
  ]);

  // Handle pagination
  const handlePreviousPage = () => {
    const newOffset = Math.max(0, pagination.offset - pagination.limit);
    fetchRecords(newOffset);
  };

  const handleNextPage = () => {
    if (pagination.hasMore) {
      const newOffset = pagination.offset + pagination.limit;
      fetchRecords(newOffset);
    }
  };

  // Handle limit change
  const handleLimitChange = (newLimit: number) => {
    // Update pagination state - useEffect will trigger the fetch automatically
    setPagination((prev) => ({ ...prev, limit: newLimit, offset: 0 }));
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setJobFamilyFilter("");
    setSubFamilyFilter("");
  };

  // Check if any filter is active
  const hasActiveFilters = jobFamilyFilter || subFamilyFilter;

  // Calculate current page info
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit) || 1;

  // Handle column sort
  const handleSort = (column: string) => {
    if (sortBy === column) {
      // Toggle sort order if same column
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // New column, default to ascending
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  // Render sortable column header
  const renderSortableHeader = (label: string, column: string) => {
    const isActive = sortBy === column;
    return (
      <TableCell
        className={`${styles.tableHeaderCell} ${styles.sortableHeader}`}
        onClick={() => handleSort(column)}
      >
        <Box className={styles.sortableHeaderContent}>
          {label}
          <Box className={styles.sortIcon}>
            {isActive ? (
              sortOrder === "asc" ? (
                <ArrowUpwardIcon fontSize="small" />
              ) : (
                <ArrowDownwardIcon fontSize="small" />
              )
            ) : (
              <Box sx={{ width: 20, height: 20 }} /> // Placeholder for alignment
            )}
          </Box>
        </Box>
      </TableCell>
    );
  };

  // Dialog handlers
  const handleOpenAddDialog = () => {
    setFormData({
      job_title: "",
      level_title: "",
      job_family: "",
      sub_family: "",
      grade_level: 11,
      ctc_midpoint_aed: 0,
    });
    setDialogMode("add");
  };

  // Inline editing handlers
  const handleStartEdit = (record: MappingRecord) => {
    setEditingRowId(record.id);
    setEditedCtc(record.ctc_midpoint_aed);
  };

  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditedCtc(0);
  };

  const handleSaveCtc = async (record: MappingRecord) => {
    // Validate CTC value
    if (!editedCtc || editedCtc <= 0) {
      setError("Please enter a valid CTC amount greater than 0");
      return;
    }

    // Check if value actually changed
    if (editedCtc === record.ctc_midpoint_aed) {
      handleCancelEdit();
      return;
    }

    try {
      setSavingCtc(true);
      setError(null);

      await axiosClient.put(`/commercial-calculator-rate-card/${record.id}`, {
        job_title: record.job_title,
        level_title: record.level_title,
        job_family: record.job_family,
        sub_family: record.sub_family,
        grade_level: record.grade_level,
        ctc_midpoint_aed: editedCtc,
      });

      setSuccess("CTC updated successfully");
      setEditingRowId(null);
      setEditedCtc(0);
      fetchRecords(pagination.offset);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error("Error updating CTC:", err);
      setError(err.response?.data?.message || "Failed to update CTC");
    } finally {
      setSavingCtc(false);
    }
  };

  const handleOpenDeleteDialog = (record: MappingRecord) => {
    setSelectedRecord(record);
    setDialogMode("delete");
  };

  const handleCloseDialog = () => {
    setDialogMode(null);
    setSelectedRecord(null);
    setFormData({});
  };

  // Form field handlers
  const handleFormChange = (field: keyof MappingRecord, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Submit handlers
  const handleAddSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);

      await axiosClient.post("/commercial-calculator-rate-card", formData);

      setSuccess("Mapping added successfully");
      handleCloseDialog();
      fetchRecords(pagination.offset);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error("Error adding mapping:", err);
      setError(err.response?.data?.message || "Failed to add mapping");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!selectedRecord) return;

    try {
      setSubmitting(true);
      setError(null);

      await axiosClient.delete(
        `/commercial-calculator-rate-card/${selectedRecord.id}`,
      );

      setSuccess("Mapping deleted successfully");
      handleCloseDialog();
      fetchRecords(pagination.offset);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error("Error deleting mapping:", err);
      setError(err.response?.data?.message || "Failed to delete mapping");
    } finally {
      setSubmitting(false);
    }
  };

  // Validation (only for Add dialog now)
  const isFormValid = useMemo(() => {
    if (dialogMode === "add") {
      return (
        formData.job_title &&
        formData.job_title.trim() !== "" &&
        formData.level_title &&
        formData.level_title.trim() !== "" &&
        formData.job_family &&
        formData.job_family.trim() !== "" &&
        formData.sub_family &&
        formData.sub_family.trim() !== "" &&
        formData.grade_level &&
        formData.grade_level > 0 &&
        formData.ctc_midpoint_aed &&
        formData.ctc_midpoint_aed > 0
      );
    }
    return true;
  }, [dialogMode, formData]);

  // Loading skeleton
  const renderLoadingSkeleton = () => (
    <TableContainer component={Paper} className={styles.tableContainer}>
      <Table>
        <TableHead>
          <TableRow className={styles.tableHeaderRow}>
            {renderSortableHeader("JOB FAMILY", "job_family")}
            {renderSortableHeader("SUB FAMILY", "sub_family")}
            {renderSortableHeader("GRADE", "grade_level")}
            {renderSortableHeader("MONTHLY CTC (AED)", "ctc_midpoint_aed")}
            <TableCell className={styles.tableHeaderCell} align="right">
              ACTIONS
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...Array(5)].map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton variant="text" width={180} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={150} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={40} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={100} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width={150} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  return (
    <Box className={styles.container}>
      {/* Header */}
      {/* <Box className={styles.header}>
        <Box>
          <Typography variant="h4" className={styles.title}>
            Rate Card Administration
          </Typography>
          <Typography className={styles.subtitle}>
            Visible only to the Rewards Admin persona
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={handleOpenAddDialog}
          className={styles.addButton}
        >
          + Add Entry
        </Button>
      </Box> */}

      {/* Success/Error Alerts */}
      {success && (
        <Alert
          severity="success"
          onClose={() => setSuccess(null)}
          sx={{ mb: 2 }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Rate Card Section */}
      <Box className={styles.cardSection}>
        <Typography variant="h6" className={styles.cardTitle}>
          Rate Card
        </Typography>
        <Typography className={styles.cardSubtitle}>
          Job Family, Sub Family, and Grade are fixed reference data. Only
          Monthly CTC can be edited here.
        </Typography>

        {/* Filter Section */}
        <Box className={styles.filterSection}>
          <Box className={styles.filterContainer}>
            <TextField
              size="small"
              placeholder="Filter by Job Family"
              value={jobFamilyFilter}
              onChange={(e) => setJobFamilyFilter(e.target.value)}
              className={styles.filterInput}
              InputProps={{
                endAdornment: jobFamilyFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setJobFamilyFilter("")}
                      edge="end"
                      className={styles.clearIcon}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              placeholder="Filter by Sub Family"
              value={subFamilyFilter}
              onChange={(e) => setSubFamilyFilter(e.target.value)}
              className={styles.filterInput}
              InputProps={{
                endAdornment: subFamilyFilter && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSubFamilyFilter("")}
                      edge="end"
                      className={styles.clearIcon}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {hasActiveFilters && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListOffIcon />}
              onClick={handleClearAllFilters}
              className={styles.clearAllButton}
            >
              Clear All Filters
            </Button>
          )}
        </Box>

        {/* Table */}
        {loading ? (
          renderLoadingSkeleton()
        ) : records.length === 0 ? (
          <Box className={styles.emptyState}>
            <Typography className={styles.emptyStateText}>
              No rate card mappings found.
              {(debouncedJobFamily || debouncedSubFamily) &&
                " Try adjusting your filters."}
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table>
              <TableHead>
                <TableRow className={styles.tableHeaderRow}>
                  {renderSortableHeader("JOB FAMILY", "job_family")}
                  {renderSortableHeader("SUB FAMILY", "sub_family")}
                  {renderSortableHeader("GRADE", "grade_level")}
                  {renderSortableHeader(
                    "MONTHLY CTC (AED)",
                    "ctc_midpoint_aed",
                  )}
                  <TableCell className={styles.tableHeaderCell} align="right">
                    ACTIONS
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => {
                  const isEditing = editingRowId === record.id;

                  return (
                    <TableRow key={record.id} className={styles.tableRow}>
                      <TableCell className={styles.tableCell}>
                        {record.job_family}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {record.sub_family}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {record.grade_level}
                      </TableCell>
                      <TableCell className={styles.tableCell}>
                        {isEditing ? (
                          <TextField
                            type="number"
                            size="small"
                            value={editedCtc}
                            onChange={(e) =>
                              setEditedCtc(parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ min: 0, step: 0.01 }}
                            autoFocus
                            className={styles.inlineInput}
                          />
                        ) : (
                          record.ctc_midpoint_aed.toLocaleString("en-US", {
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 2,
                          })
                        )}
                      </TableCell>
                      <TableCell className={styles.tableCell} align="right">
                        {isEditing ? (
                          <>
                            <Button
                              size="small"
                              onClick={() => handleSaveCtc(record)}
                              disabled={savingCtc}
                              className={styles.saveButton}
                            >
                              {savingCtc ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              size="small"
                              onClick={handleCancelEdit}
                              disabled={savingCtc}
                              className={styles.cancelButton}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="small"
                              onClick={() => handleStartEdit(record)}
                              disabled={editingRowId !== null}
                              className={styles.editButton}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              onClick={() => handleOpenDeleteDialog(record)}
                              disabled={editingRowId !== null}
                              className={styles.removeButton}
                            >
                              Remove
                            </Button>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {!loading && records.length > 0 && (
          <Box className={styles.pagination}>
            <Box className={styles.paginationLeft}>
              <Typography className={styles.paginationLabel}>
                Rows per page:
              </Typography>
              <FormControl size="small" className={styles.limitSelector}>
                <Select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className={styles.limitSelect}
                >
                  <MenuItem value={15}>15</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box className={styles.paginationCenter}>
              <IconButton
                onClick={handlePreviousPage}
                disabled={pagination.offset === 0}
                size="small"
                className={styles.paginationButton}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography className={styles.paginationText}>
                Page {currentPage} of {totalPages} ({pagination.total} record
                {pagination.total !== 1 ? "s" : ""})
              </Typography>
              <IconButton
                onClick={handleNextPage}
                disabled={!pagination.hasMore}
                size="small"
                className={styles.paginationButton}
              >
                <ArrowForwardIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        )}
      </Box>

      {/* Add Entry Dialog */}
      <Dialog
        open={dialogMode === "add"}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
            <TextField
              label="Job Title"
              fullWidth
              value={formData.job_title || ""}
              onChange={(e) => handleFormChange("job_title", e.target.value)}
              required
            />
            <TextField
              label="Level Title"
              fullWidth
              value={formData.level_title || ""}
              onChange={(e) => handleFormChange("level_title", e.target.value)}
              required
            />
            <TextField
              label="Job Family"
              fullWidth
              value={formData.job_family || ""}
              onChange={(e) => handleFormChange("job_family", e.target.value)}
              required
            />
            <TextField
              label="Sub Family"
              fullWidth
              value={formData.sub_family || ""}
              onChange={(e) => handleFormChange("sub_family", e.target.value)}
              required
            />
            <TextField
              label="Grade Level"
              type="number"
              fullWidth
              value={formData.grade_level || ""}
              onChange={(e) =>
                handleFormChange("grade_level", parseInt(e.target.value, 10))
              }
              inputProps={{ min: 1 }}
              required
            />
            <TextField
              label="Monthly CTC (AED)"
              type="number"
              fullWidth
              value={formData.ctc_midpoint_aed || ""}
              onChange={(e) =>
                handleFormChange("ctc_midpoint_aed", parseFloat(e.target.value))
              }
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSubmit}
            variant="contained"
            disabled={submitting || !isFormValid}
          >
            {submitting ? "Adding..." : "Add Mapping"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={dialogMode === "delete"}
        onClose={handleCloseDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this mapping?</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            <strong>Job Family:</strong> {selectedRecord?.job_family}
          </Typography>
          <Typography variant="body2">
            <strong>Sub Family:</strong> {selectedRecord?.sub_family}
          </Typography>
          <Typography variant="body2">
            <strong>Grade:</strong> {selectedRecord?.grade_level}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSubmit}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
